/*!
 * clyro-shop.js - the ONE fixed purchase module for template-simple.
 *
 * This file is FIXED infrastructure. The chat agent never rewrites it; sections
 * only wire markup to the hooks below. Vanilla fetch + Alpine, no other deps.
 *
 * Alpine store `cart`:
 *   { items:[], item_count:0, total_price:0, open:false, loading:false, loadingKey:null }
 *
 * window.clyroShop public surface (sections + layout depend on these EXACT names):
 *   add(variantId, qty=1)        -> POST /cart/add.js    -> refresh() -> openCart()
 *   addMany([{id,quantity}])     -> POST /cart/add.js {items} -> one line per variant (packs)
 *   change(lineKey, qty)         -> POST /cart/change.js -> refresh()
 *   refresh()                    -> GET  /cart.js        -> updates Alpine.store('cart')
 *   openCart()                   -> Alpine.store('cart').open = true
 *   closeCart()                  -> Alpine.store('cart').open = false
 *   money(cents)                 -> Shopify-style price string
 *   variantFrom(optionValues, product) -> { id, price, available,
 *       compare_at_price, featured_image } | null
 *   buyBox(seed)                 -> Alpine x-data factory for the product
 *       buy-box (variant pick, live price, gallery sync, bundle-tier qty).
 *       seed = { qty, tier, sel: [option values], price: fallback cents }.
 *       The section supplies markup + <script type="application/json"
 *       x-ref="pdata">{"variants": [...]}</script>; behavior lives HERE.
 */
(function () {
  var BASE_CART = { items: [], item_count: 0, total_price: 0, open: false, loading: false, loadingKey: null };

  function store() {
    return (window.Alpine && window.Alpine.store) ? window.Alpine.store('cart') : null;
  }

  var giftSyncing = false;
  var productPriceCache = {};

  function hydrateCompareAt(items) {
    if (!Array.isArray(items) || !items.length) return;
    var handles = [];
    items.forEach(function (line) {
      var h = line && line.handle;
      if (h && !productPriceCache[h] && handles.indexOf(h) === -1) handles.push(h);
    });
    if (!handles.length) return;
    Promise.all(handles.map(function (handle) {
      return fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (prod) {
          var map = {};
          if (prod && Array.isArray(prod.variants)) {
            prod.variants.forEach(function (v) { map[String(v.id)] = Number(v.compare_at_price) || 0; });
          }
          productPriceCache[handle] = map;
        })
        .catch(function () { productPriceCache[handle] = {}; });
    })).then(function () {
      var c = store();
      if (!c || !Array.isArray(c.items)) return;
      c.items.forEach(function (line) {
        var map = productPriceCache[line.handle] || {};
        line._seu_compare_at_price = Number(map[String(line.variant_id || line.id)]) || 0;
      });
      c.items = c.items.slice();
    });
  }

  function isGiftLine(line, giftVariantId) {
    if (!line || !giftVariantId) return false;
    var sameVariant = String(line.variant_id || line.id) === String(giftVariantId);
    var props = line.properties || {};
    return sameVariant && String(props._seu_free_gift || '') === '1';
  }

  function qualifyingTotal(cartLike, giftVariantId) {
    var items = cartLike && Array.isArray(cartLike.items) ? cartLike.items : [];
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (isGiftLine(line, giftVariantId)) continue;
      var value = line.final_line_price;
      if (typeof value !== 'number') value = line.line_price;
      if (typeof value !== 'number') value = 0;
      total += value;
    }
    return total;
  }

  function syncGift(data) {
    var cfg = window.SEU_GIFT_CONFIG || {};
    var target = Number(cfg.targetCents) || 0;
    var giftId = Number(cfg.variantId) || 0;
    if (!target || !giftId || giftSyncing || !data || !Array.isArray(data.items)) return Promise.resolve(data);

    var strayGiftLine = null;
    for (var sg = 0; sg < data.items.length; sg++) {
      var sgProps = data.items[sg].properties || {};
      if (String(sgProps._seu_free_gift || '') === '1' && String(data.items[sg].variant_id || data.items[sg].id) !== String(giftId)) {
        strayGiftLine = data.items[sg];
        break;
      }
    }
    if (strayGiftLine) {
      giftSyncing = true;
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: strayGiftLine.key, quantity: 0 })
      }).then(function () { return fetch('/cart.js', { headers: { Accept: 'application/json' } }); })
        .then(function (r) { return r.json(); })
        .then(function (fresh) { applyCart(fresh); return fresh; })
        .catch(function () { return data; })
        .finally(function () { giftSyncing = false; });
    }

    var qTotal = qualifyingTotal(data, giftId);
    var giftLine = null;
    for (var i = 0; i < data.items.length; i++) {
      if (isGiftLine(data.items[i], giftId)) { giftLine = data.items[i]; break; }
    }
    var shouldHave = qTotal >= target;
    var needsAdd = shouldHave && !giftLine;
    var needsRemove = !shouldHave && !!giftLine;
    var needsQtyFix = shouldHave && giftLine && giftLine.quantity !== 1;
    if (!needsAdd && !needsRemove && !needsQtyFix) return Promise.resolve(data);

    giftSyncing = true;
    var request;
    if (needsAdd) {
      request = fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: giftId, quantity: 1, properties: { _seu_free_gift: '1' } }] })
      });
    } else {
      request = fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: giftLine.key, quantity: needsRemove ? 0 : 1 })
      });
    }

    return request.then(function () {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } });
    }).then(function (r) { return r.json(); }).then(function (fresh) {
      applyCart(fresh);
      return fresh;
    }).catch(function (err) {
      console.error('[clyro-shop] free gift sync failed', err);
      return data;
    }).finally(function () { giftSyncing = false; });
  }

  function applyCart(data) {
    var c = store();
    if (!c || !data) return;
    c.items = Array.isArray(data.items) ? data.items : [];
    c.item_count = typeof data.item_count === 'number' ? data.item_count : 0;
    c.total_price = typeof data.total_price === 'number' ? data.total_price : 0;
    hydrateCompareAt(c.items);
    // Socket contract (shared with the full base): transplanted headers carry
    // static [data-clyro-cart-count] badges — keep them live — and external
    // listeners (transfer smoke, integrations) hear 'cart:updated'.
    try {
      var badges = document.querySelectorAll('[data-clyro-cart-count]');
      for (var i = 0; i < badges.length; i++) badges[i].textContent = String(c.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    } catch (e) { /* never let socket sync break the cart */ }
    // Keep the selected reward product in sync with the € threshold.
    setTimeout(function () { syncGift(data); }, 0);
  }

  function setLoading(on, key) {
    var c = store();
    if (!c) return;
    if (on) {
      c.loading = true;
      if (key != null) c.loadingKey = String(key);
    } else {
      c.loading = false;
      c.loadingKey = null;
    }
  }

  // ── money ────────────────────────────────────────────────────────
  // Shopify ships prices in cents. Use the live shop currency symbol if the
  // storefront exposed it, otherwise fall back to a plain "$" prefix.
  function money(cents) {
    var n = Number(cents);
    if (!isFinite(n)) n = 0;
    var amount = (n / 100).toFixed(2);
    var symbol = '$';
    try {
      var cur = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
      if (cur === 'EUR') symbol = '€';
      else if (cur === 'GBP') symbol = '£';
      else if (cur === 'JPY') { symbol = '¥'; amount = String(Math.round(n / 100)); }
    } catch (_) {}
    return symbol + amount;
  }

  // ── variant resolution ───────────────────────────────────────────
  // optionValues = array of selected option strings, in option order.
  // product = a Shopify product JSON object (product.variants[]).
  function variantFrom(optionValues, product) {
    if (!product || !Array.isArray(product.variants)) return null;
    var opts = optionValues || [];
    var match = product.variants.find(function (v) {
      return (opts[0] == null || v.option1 === opts[0])
          && (opts[1] == null || v.option2 === opts[1])
          && (opts[2] == null || v.option3 === opts[2]);
    });
    if (!match) return null;
    return {
      id: match.id,
      option1: match.option1,
      option2: match.option2,
      option3: match.option3,
      price: match.price,
      available: !!match.available,
      compare_at_price: match.compare_at_price || 0,
      featured_image: match.featured_image || null
    };
  }

  // ── cart drawer toggles ──────────────────────────────────────────
  function openCart() {
    var c = store();
    if (c) c.open = true;
  }
  function closeCart() {
    var c = store();
    if (c) c.open = false;
  }

  // ── cart network ops (Shopify AJAX API) ──────────────────────────
  function refresh() {
    var c = store();
    if (c) c.loading = true;
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) { applyCart(data); return data; })
      .catch(function (err) { console.error('[clyro-shop] refresh failed', err); })
      .finally(function () { setLoading(false); });
  }

  // ── add-to-cart flying product animation ────────────────────────
  // Starts instantly from the pressed Add to cart button, using the currently
  // selected product image, then flies across the full viewport into the cart.
  var lastAddButton = null;
  document.addEventListener('pointerdown', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('.pdp-atc, .seu-sticky__btn, [onclick*="clyroShop.add"]');
    if (btn) lastAddButton = btn;
  }, true);
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('.pdp-atc, .seu-sticky__btn, [onclick*="clyroShop.add"]');
    if (btn) lastAddButton = btn;
  }, true);

  function flyProductToCart() {
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return null;
      var target = document.getElementById('clyro-cart-toggle');
      var pdp = document.querySelector('.seu-pdp');
      var source = pdp && (pdp.querySelector('.seu-gallery__image.is-active') || pdp.querySelector('.seu-gallery__image'));
      if (!target || !source || !source.getBoundingClientRect) return null;

      var sr = source.getBoundingClientRect();
      var tr = target.getBoundingClientRect();
      var br = lastAddButton && lastAddButton.getBoundingClientRect ? lastAddButton.getBoundingClientRect() : null;
      if (!sr.width || !sr.height || !tr.width || !tr.height) return null;

      // Bigger than v1 so the exact item is easy to see on a phone.
      var size = Math.max(76, Math.min(96, Math.min(window.innerWidth * 0.22, 96)));
      // Begin at the actual Add to cart button instead of halfway up the gallery.
      var startX = br && br.width ? br.left + br.width * 0.5 - size * 0.5 : sr.left + sr.width * 0.5 - size * 0.5;
      var startY = br && br.height ? br.top + br.height * 0.5 - size * 0.5 : sr.top + sr.height * 0.72 - size * 0.5;
      var endX = tr.left + tr.width * 0.5 - size * 0.5;
      var endY = tr.top + tr.height * 0.5 - size * 0.5;

      var flyer = document.createElement('img');
      flyer.className = 'seu-cart-flyer';
      flyer.src = source.currentSrc || source.src;
      flyer.alt = '';
      flyer.setAttribute('aria-hidden', 'true');
      flyer.style.width = size + 'px';
      flyer.style.height = size + 'px';
      flyer.style.left = startX + 'px';
      flyer.style.top = startY + 'px';
      document.body.appendChild(flyer);

      var dx = endX - startX, dy = endY - startY;
      // Slower, eye-trackable glide: keep the exact product clearly visible
      // for almost the whole trip, then shrink only as it reaches the cart.
      var anim = flyer.animate([
        { transform: 'translate3d(0,0,0) scale(1)', opacity: 1, offset: 0 },
        { transform: 'translate3d(' + (dx * .28) + 'px,' + (dy * .22) + 'px,0) scale(1)', opacity: 1, offset: .28 },
        { transform: 'translate3d(' + (dx * .60) + 'px,' + (dy * .54) + 'px,0) scale(.92)', opacity: 1, offset: .60 },
        { transform: 'translate3d(' + (dx * .84) + 'px,' + (dy * .82) + 'px,0) scale(.72)', opacity: 1, offset: .84 },
        { transform: 'translate3d(' + dx + 'px,' + dy + 'px,0) scale(.28)', opacity: .45, offset: 1 }
      ], { duration: 820, easing: 'cubic-bezier(.22,.62,.24,1)', fill: 'forwards' });

      var finished = false;
      function done() {
        if (finished) return;
        finished = true;
        if (flyer && flyer.parentNode) flyer.parentNode.removeChild(flyer);
        // Make the landing reaction unmistakable. Animate the actual cart control
        // (not just a CSS class that can be overridden by header styles).
        try {
          target.animate([
            {transform:'scale(1) rotate(0deg)'},
            {transform:'scale(1.34) rotate(-8deg)', offset:.30},
            {transform:'scale(.92) rotate(5deg)', offset:.58},
            {transform:'scale(1.12) rotate(-2deg)', offset:.78},
            {transform:'scale(1) rotate(0deg)'}
          ], {duration:720,easing:'cubic-bezier(.2,.8,.2,1)'});
        } catch(_) {}
        var badge=document.getElementById('clyro-cart-count');
        if(badge){ try{ badge.animate([{transform:'scale(1)'},{transform:'scale(1.65)',offset:.45},{transform:'scale(1)'}],{duration:680,easing:'cubic-bezier(.2,.9,.25,1)'}); }catch(_){} }
      }
      anim.onfinish = done;
      anim.oncancel = done;
      setTimeout(done, 620);
      return anim;
    } catch (_) {
      return null;
    }
  }

  function ensureFlyStyles() {
    if (document.getElementById('seu-cart-fly-styles')) return;
    var style = document.createElement('style');
    style.id = 'seu-cart-fly-styles';
    style.textContent = '.seu-cart-flyer{position:fixed;z-index:2147483000;object-fit:contain;background:#fff;border:1px solid rgba(0,0,0,.10);border-radius:50%;padding:5px;box-sizing:border-box;pointer-events:none;box-shadow:0 8px 26px rgba(0,0,0,.20);will-change:transform,opacity}.seu-cart-hit{animation:seuCartHit .28s cubic-bezier(.2,.8,.2,1)}@keyframes seuCartHit{0%{transform:scale(1)}45%{transform:scale(1.2)}100%{transform:scale(1)}}.seu-badge-pop{animation:seuBadgePop .38s cubic-bezier(.2,.9,.25,1)}@keyframes seuBadgePop{0%{transform:scale(.7)}55%{transform:scale(1.45)}100%{transform:scale(1)}}.seu-atc-added{transform:scale(.985);transition:transform .16s ease}.seu-recent{max-width:1200px;margin:10px auto 70px;padding:0 24px;font-family:Arial,Helvetica,sans-serif}.seu-recent h2{font-size:22px;margin:0 0 18px}.seu-recent__row{display:flex;gap:14px;overflow-x:auto;scroll-snap-type:x mandatory;padding-bottom:8px;-webkit-overflow-scrolling:touch}.seu-recent__card{flex:0 0 170px;scroll-snap-align:start;color:#111;text-decoration:none;display:grid;grid-template-rows:170px 36px 20px;align-content:start}.seu-recent__card img{width:100%;height:170px;object-fit:contain;background:#fafafa;border-radius:8px}.seu-recent__card b{display:-webkit-box;font-size:13px;line-height:16px;margin-top:7px;overflow:hidden;-webkit-line-clamp:2;-webkit-box-orient:vertical}.seu-recent__card span{display:flex;align-items:baseline;gap:5px;font-size:13px;margin-top:3px;white-space:nowrap}.seu-recent__price em{font-style:normal;color:#111}.seu-recent__price s{color:#999;text-decoration:line-through;text-decoration-thickness:1px}.seu-recent__card:not(:has(img)){grid-template-rows:170px 36px 20px}@media(max-width:600px){.seu-recent{padding:0 20px;margin-bottom:55px}.seu-recent__card{flex-basis:145px;grid-template-rows:145px 36px 20px}.seu-recent__card img{height:145px}}';
    document.head.appendChild(style);
  }
  ensureFlyStyles();

  // ── premium micro-interactions ───────────────────────────────
  function haptic(ms) {
    try { if (navigator.vibrate) navigator.vibrate(ms || 12); } catch (_) {}
  }

  function setAddedState(button) {
    if (!button) return;
    var label = button.querySelector('.pdp-atc__label');
    if (!label) return;
    if (!button.dataset.seuOriginalLabel) button.dataset.seuOriginalLabel = label.textContent;
    label.textContent = '✓ Added';
    button.classList.add('seu-atc-added');
    clearTimeout(button._seuAddedTimer);
    button._seuAddedTimer = setTimeout(function () {
      label.textContent = button.dataset.seuOriginalLabel || 'Add to cart';
      button.classList.remove('seu-atc-added');
    }, 1050);
  }

  function popCartBadge() {
    var badge = document.getElementById('clyro-cart-count');
    if (!badge) return;
    badge.classList.remove('seu-badge-pop'); void badge.offsetWidth; badge.classList.add('seu-badge-pop');
    setTimeout(function(){ badge.classList.remove('seu-badge-pop'); }, 420);
  }

  function animateGiftProgress(fromWidth) {
    var bar=document.querySelector('.seu-cart__progress i');
    if(!bar) return;
    var target=bar.style.width || getComputedStyle(bar).width;
    var targetPct = target && target.indexOf('%')>-1 ? target : null;
    if(!targetPct){
      var parent=bar.parentElement, pw=parent?parent.getBoundingClientRect().width:0, bw=bar.getBoundingClientRect().width;
      targetPct=pw ? ((bw/pw)*100).toFixed(2)+'%' : '0%';
    }
    var start=(typeof fromWidth==='number' && isFinite(fromWidth)) ? Math.max(0,Math.min(100,fromWidth))+'%' : '0%';
    bar.style.transition='none'; bar.style.width=start;
    void bar.offsetWidth;
    requestAnimationFrame(function(){ requestAnimationFrame(function(){
      bar.style.transition='width 900ms cubic-bezier(.22,.8,.22,1)';
      bar.style.width=targetPct;
    }); });
  }

  function premiumSuccess(variantId) {
    haptic(18);
    setAddedState(lastAddButton);
    popCartBadge();
    try { window.dispatchEvent(new CustomEvent('seu:cart-added', { detail: { variantId: String(variantId) } })); } catch (_) {}
  }

  // Mobile product-gallery swipe. It asks Alpine's existing gallery buttons to
  // change image, so variant/image state remains owned by the theme.
  (function gallerySwipe(){
    var sx=0, sy=0, gallery=null;
    document.addEventListener('touchstart', function(e){
      gallery=e.target && e.target.closest && e.target.closest('.seu-gallery__main');
      if(!gallery || !e.touches || !e.touches[0]) return;
      sx=e.touches[0].clientX; sy=e.touches[0].clientY;
    }, {passive:true});
    document.addEventListener('touchend', function(e){
      if(!gallery || !e.changedTouches || !e.changedTouches[0]) { gallery=null; return; }
      var dx=e.changedTouches[0].clientX-sx, dy=e.changedTouches[0].clientY-sy;
      if(Math.abs(dx)>48 && Math.abs(dx)>Math.abs(dy)*1.25){
        var root=gallery.closest('.seu-gallery'), thumbs=root && [].slice.call(root.querySelectorAll('.seu-gallery__thumb'));
        if(thumbs && thumbs.length){
          var idx=thumbs.findIndex(function(b){return b.classList.contains('is-active')});
          if(idx<0) idx=0;
          var next=dx<0 ? Math.min(thumbs.length-1,idx+1) : Math.max(0,idx-1);
          if(next!==idx) { haptic(8); thumbs[next].click(); }
        }
      }
      gallery=null;
    }, {passive:true});
  })();

  // Recently viewed: lightweight, local to the shopper's browser; no app.
  function initRecentlyViewed(){
    var pdp=document.querySelector('.seu-pdp'); if(!pdp) return;
    try{
      var title=(document.querySelector('.seu-title')||{}).textContent||document.title;
      var url=location.pathname;
      var img=document.querySelector('.seu-gallery__image');
      var priceBox=document.querySelector('.seu-price');
      var currentNode=priceBox&&priceBox.querySelector('.seu-price__current');
      var compareNode=priceBox&&priceBox.querySelector('.seu-price__compare');
      var price=(priceBox||{}).textContent||'';
      var item={title:title.trim(),url:url,img:img?(img.currentSrc||img.src):'',price:price.trim(),priceCurrent:currentNode?currentNode.textContent.trim():'',priceCompare:(compareNode&&getComputedStyle(compareNode).display!=='none')?compareNode.textContent.trim():''};
      var key='seu_recently_viewed_v1', list=JSON.parse(localStorage.getItem(key)||'[]');
      list=list.filter(function(x){return x && x.url!==url});
      var previous=list.slice(0,8);
      list.unshift(item); localStorage.setItem(key,JSON.stringify(list.slice(0,9)));
      if(!previous.length) return;
      var wrap=document.createElement('section'); wrap.className='seu-recent';
      wrap.innerHTML='<h2>Recently viewed</h2><div class="seu-recent__row"></div>';
      var row=wrap.querySelector('.seu-recent__row');
      previous.forEach(function(x){
        var a=document.createElement('a'); a.className='seu-recent__card'; a.href=x.url;
        a.innerHTML=(x.img?'<img loading="lazy" src="'+String(x.img).replace(/"/g,'&quot;')+'" alt="">':'')+'<b></b><span class="seu-recent__price"><em></em><s></s></span>';
        a.querySelector('b').textContent=x.title||'';
        var cur=x.priceCurrent||'', cmp=x.priceCompare||'';
        if(!cur && x.price){ var pm=String(x.price).match(/[€$£]\s*\d+(?:[.,]\d{1,2})?/g)||[]; cur=pm[0]||String(x.price); cmp=pm[1]||''; }
        a.querySelector('.seu-recent__price em').textContent=cur;
        var cs=a.querySelector('.seu-recent__price s'); cs.textContent=cmp; if(!cmp) cs.style.display='none';
        row.appendChild(a);
      });
      pdp.insertAdjacentElement('afterend',wrap);
    }catch(_){}
  }
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',initRecentlyViewed); else setTimeout(initRecentlyViewed,0);

  function add(variantId, qty) {
    var quantity = Math.max(1, Number(qty) || 1);
    // Keep Shopify variant IDs as strings. This avoids any needless numeric
    // coercion and always submits the exact ID rendered by Liquid/Alpine.
    var id = variantId != null ? String(variantId).trim() : '';
    if (!id || id === '0' || id === 'null' || id === 'undefined') {
      console.warn('[clyro-shop] add: no variant id');
      return Promise.resolve(null);
    }
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (root.charAt(root.length - 1) !== '/') root += '/';
    var endpoint = root + 'cart/add.js';
    // Remember the old gift-progress position so the refreshed cart can visibly
    // glide from the old total to the new total when the drawer opens.
    var oldGiftPct=0;
    try{ var gb=document.querySelector('.seu-cart__progress i'), gp=gb&&gb.parentElement; if(gb&&gp&&gp.getBoundingClientRect().width) oldGiftPct=(gb.getBoundingClientRect().width/gp.getBoundingClientRect().width)*100; }catch(_){}
    // Fire the visual immediately on the tap; do not wait for Shopify/network.
    flyProductToCart();
    setLoading(true, 'add');

    function parseResponse(r) {
      return r.text().then(function (text) {
        var data = null;
        try { data = text ? JSON.parse(text) : null; } catch (_) {}
        if (!r.ok) {
          var msg = data && (data.description || data.message || data.status) ? (data.description || data.message || String(data.status)) : ('Cart error ' + r.status);
          var err = new Error(msg);
          err.status = r.status;
          err.data = data;
          throw err;
        }
        return data;
      });
    }

    // Primary request: Shopify's documented JSON cart endpoint.
    return fetch(endpoint, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: quantity }] })
    })
      .then(parseResponse)
      .catch(function (firstErr) {
        // Compatibility fallback: submit the same exact variant as a native
        // Shopify form payload. Useful for stores/apps that reject JSON items.
        console.warn('[clyro-shop] JSON add failed, retrying form payload', firstErr);
        var body = new URLSearchParams();
        body.set('id', id);
        body.set('quantity', String(quantity));
        return fetch(endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8', Accept: 'application/json' },
          body: body.toString()
        }).then(parseResponse);
      })
      .then(function (item) {
        return refresh().then(function (cart) {
          // Open only after Shopify confirms the item exists in the cart.
          var found = cart && Array.isArray(cart.items) && cart.items.some(function (line) {
            return String(line.variant_id || line.id) === id;
          });
          if (found) {
            premiumSuccess(id);
            animateGiftProgress(oldGiftPct);
            // The fly animation already started instantly on tap. Open the
            // existing drawer as soon as Shopify confirms the item.
            openCart();
            return item;
          }
          return null;
        });
      })
      .catch(function (err) {
        console.error('[clyro-shop] add failed', err);
        setLoading(false);
        try { window.alert(err && err.message ? err.message : 'Could not add this item to cart. Please try again.'); } catch (_) {}
        return null;
      });
  }

  /**
   * Add SEVERAL lines in one request (packs, bundles, "buy 2 get 2").
   *
   * A pack is not one line of quantity N: each item can be a DIFFERENT variant
   * (two wraps, one beige and one black), and Shopify models that as one line
   * per variant. /cart/add.js takes an `items` array for exactly this.
   *
   * IMPORTANT, and the theme cannot change it: Shopify charges what the lines
   * are worth. A pack that DISPLAYS "$48.90 instead of $112.00" is charged
   * $112.00 unless the shop carries a matching discount (an automatic
   * "Buy X get Y", a discount function, or a real pack product). The transfer
   * report says which one to create; this function only makes the cart right.
   */
  function addMany(lines) {
    var items = [];
    for (var i = 0; i < (lines || []).length; i++) {
      var l = lines[i] || {};
      var id = Number(l.id) || l.id;
      if (!id) continue;
      var qty = Math.max(1, Number(l.quantity) || 1);
      // Same variant twice with no distinguishing properties is ONE line of
      // quantity 2, not two lines - which is also what Shopify would collapse
      // it to, so we send what we mean.
      var merged = null;
      for (var j = 0; j < items.length; j++) {
        if (String(items[j].id) === String(id) && !items[j].properties && !l.properties) { merged = items[j]; break; }
      }
      if (merged) { merged.quantity += qty; continue; }
      var item = { id: id, quantity: qty };
      if (l.properties) item.properties = l.properties;
      items.push(item);
    }
    if (!items.length) {
      console.warn('[clyro-shop] addMany: no variant ids');
      return Promise.resolve(null);
    }
    if (items.length === 1 && !items[0].properties) return add(items[0].id, items[0].quantity);
    setLoading(true, 'add');
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) { return refresh().then(function () { openCart(); return data; }); })
      .catch(function (err) { console.error('[clyro-shop] addMany failed', err); setLoading(false); });
  }

  function change(lineKey, qty) {
    var quantity = Math.max(0, Number(qty) || 0);
    if (!lineKey) {
      console.warn('[clyro-shop] change: no line key');
      return Promise.resolve(null);
    }
    setLoading(true, lineKey);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) { applyCart(data); return data; })
      .catch(function (err) { console.error('[clyro-shop] change failed', err); })
      .finally(function () { setLoading(false); });
  }

  // ── product buy-box (Alpine x-data factory) ──────────────────────
  // Aura-grade behaviors, fixed here so sections stay markup-only:
  // ?variant= URL sync (read at init, replaceState on pick), a
  // 'variant:change' event for other sections, sold-out option-value
  // detection, percent OR fixed tier discounts, per-unit tier pricing.
  function buyBox(seed) {
    seed = seed || {};
    function tierCents(q, price, amt, dtype) {
      if (dtype === 'fixed') return Math.max(0, Math.round(q * price - amt * 100));
      return Math.round(q * price * (100 - amt) / 100);
    }
    return {
      qty: seed.qty || 1,
      tier: (typeof seed.tier === 'number') ? seed.tier : -1,
      activeImg: 0,
      sel: seed.sel || [],
      cv: null,
      pdata: { variants: [], media_ids: [] },
      mediaIndex: function (v) {
        if (!v) return this.activeImg;

        // Best case: Shopify has a featured image assigned directly to the variant.
        if (v.featured_image) {
          var img = v.featured_image;
          if (typeof img.position === 'number' && img.position > 0) return img.position - 1;
          var ids = this.pdata.media_ids;
          if (Array.isArray(ids) && img.id != null) {
            var i = ids.findIndex(function (id) { return id === img.id || String(id) === String(img.id); });
            if (i >= 0) return i;
          }
        }

        // CSV-imported products often have no variant-image assignment even though
        // their product images are ordered by Color. Fall back to matching the
        // selected first option (Color/Scent/etc.) to the same-position media.
        // Example: Black, Blue, Glossy -> image 1, 2, 3.
        var selectedFirst = (v.option1 != null) ? v.option1 : (this.sel && this.sel[0]);
        if (selectedFirst != null && Array.isArray(this.pdata.variants) && Array.isArray(this.pdata.media_ids)) {
          var values = [];
          this.pdata.variants.forEach(function (variant) {
            var value = variant.option1;
            if (value != null && values.indexOf(value) === -1) values.push(value);
          });
          if (values.length > 1 && this.pdata.media_ids.length >= values.length) {
            var colorIndex = values.indexOf(selectedFirst);
            if (colorIndex >= 0) return colorIndex;
          }
        }

        return this.activeImg;
      },
      syncImg: function (v) {
        var idx = this.mediaIndex(v);
        if (typeof idx === 'number' && idx >= 0) this.activeImg = idx;
      },
      init: function () {
        try { this.pdata = JSON.parse(this.$refs.pdata.textContent) || { variants: [], media_ids: [] }; } catch (_) {}
        var urlId = null;
        try { urlId = new URLSearchParams(window.location.search).get('variant'); } catch (_) {}
        var fromUrl = urlId && this.pdata.variants.find(function (v) { return String(v.id) === urlId; });
        if (fromUrl) {
          this.sel = [fromUrl.option1, fromUrl.option2, fromUrl.option3].filter(function (o) { return o != null; });
        }
        this.cv = variantFrom(this.sel, this.pdata) || this.pdata.variants[0] || null;
        this.syncImg(this.cv);
      },
      pick: function (i, val) {
        this.sel[i] = val;
        var v = variantFrom(this.sel, this.pdata);
        if (v) {
          this.cv = v;
          this.syncImg(v);
          try {
            var url = new URL(window.location);
            url.searchParams.set('variant', v.id);
            history.replaceState({}, '', url.toString());
          } catch (_) {}
          this.$dispatch && this.$dispatch('variant:change', { variant: v });
        }
      },
      optAvailable: function (i, val) {
        var probe = this.sel.slice();
        probe[i] = val;
        var v = variantFrom(probe, this.pdata);
        return !!(v && v.available);
      },
      price: function () { return this.cv ? this.cv.price : (seed.price || 0); },
      compareAt: function () { return (this.cv && this.cv.compare_at_price) || 0; },
      saving: function () { var c = this.compareAt(); return c > this.price() ? Math.round((1 - this.price() / c) * 100) : 0; },
      tierTotal: function (q, amt, dtype) { return money(tierCents(q, this.price(), amt || 0, dtype)); },
      tierUnit: function (q, amt, dtype) { return money(Math.round(tierCents(q, this.price(), amt || 0, dtype) / Math.max(1, q))); },
      available: function () { return this.cv ? this.cv.available !== false : true; }
    };
  }

  function lineBeforePrice(line, giftVariantId) {
    if (!line || isGiftLine(line, giftVariantId)) return 0;
    var qty = Math.max(1, Number(line.quantity) || 1);
    var compare = (Number(line._seu_compare_at_price) || 0) * qty;
    var original = Number(line.original_line_price) || 0;
    var finalPrice = Number(line.final_line_price != null ? line.final_line_price : line.line_price) || 0;
    if (compare > finalPrice) return compare;
    if (original > finalPrice) return original;
    return 0;
  }

  function lineSavings(line, giftVariantId) {
    if (!line || isGiftLine(line, giftVariantId)) return 0;
    var before = lineBeforePrice(line, giftVariantId);
    var finalPrice = Number(line.final_line_price != null ? line.final_line_price : line.line_price) || 0;
    return before > finalPrice ? Math.max(0, before - finalPrice) : 0;
  }

  function checkoutWithGift() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        applyCart(data);
        return syncGift(data);
      })
      .then(function () { window.location.href = '/checkout'; })
      .catch(function () { window.location.href = '/checkout'; });
  }

  window.clyroShop = {
    add: add,
    addMany: addMany,
    change: change,
    refresh: refresh,
    openCart: openCart,
    closeCart: closeCart,
    money: money,
    variantFrom: variantFrom,
    qualifyingTotal: qualifyingTotal,
    isGiftLine: isGiftLine,
    checkoutWithGift: checkoutWithGift,
    syncGift: syncGift,
    lineBeforePrice: lineBeforePrice,
    lineSavings: lineSavings,
    buyBox: buyBox
  };

  // Register the cart store and prime it on first load so the count is live.
  document.addEventListener('alpine:init', function () {
    window.Alpine.store('cart', Object.assign({}, BASE_CART));
    refresh();
  });

  // Socket contract (shared with the full base): any [data-clyro-cart-open]
  // trigger opens the drawer — transplanted Canvas headers keep working even
  // though their skin markup never knew clyroShop.
  document.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target.closest('[data-clyro-cart-open]') : null;
    if (!t) return;
    e.preventDefault();
    openCart();
  });
})();

/* Supplier Europe v8 — collection/home Quick View + Quick Add */
(function(){
  if(window.__seuQuickView) return; window.__seuQuickView=1;
  var css=document.createElement('style'); css.id='seu-quick-view-styles'; css.textContent=`
  .seu-qv-card{position:relative;display:flex;flex-direction:column}.seu-qv-card__link{display:block}.seu-qv-card h3{min-height:2.5em}.seu-quick-add{display:inline-flex;align-items:center;justify-content:center;gap:4px;width:auto;min-width:0;align-self:flex-start;margin-top:6px;height:24px;padding:0 7px;border:1px solid rgba(17,17,17,.28);background:#fff;color:#111;border-radius:999px;font:600 9.5px/1 Arial,sans-serif;cursor:pointer;transition:background .18s,color .18s,border-color .18s,transform .12s}.seu-quick-add svg{width:11px;height:11px;display:block;fill:none;stroke:currentColor;stroke-width:1.7}.seu-quick-add:hover{background:#111;color:#fff;border-color:#111}.seu-quick-add:active{transform:scale(.97)}
  .seu-qv-backdrop{position:fixed;inset:0;z-index:2147482000;background:rgba(0,0,0,.42);opacity:0;transition:opacity .22s ease;display:flex;align-items:flex-end;justify-content:center}.seu-qv-backdrop.is-open{opacity:1}.seu-qv{width:min(100%,560px);max-height:88dvh;overflow:auto;background:#fff;border-radius:20px 20px 0 0;padding:18px 20px calc(22px + env(safe-area-inset-bottom));transform:translateY(24px);transition:transform .26s cubic-bezier(.2,.8,.2,1);box-shadow:0 -16px 45px rgba(0,0,0,.18);font-family:var(--font,Arial,sans-serif)}.seu-qv-backdrop.is-open .seu-qv{transform:translateY(0)}
  .seu-qv__top{display:flex;justify-content:flex-end}.seu-qv__close{width:36px;height:36px;border:0;border-radius:50%;background:#f3f3f3;font-size:23px;line-height:1;cursor:pointer}.seu-qv__body{display:grid;grid-template-columns:150px 1fr;gap:18px}.seu-qv__img{width:150px;height:150px;object-fit:contain;background:#fafafa;border-radius:12px;transform-origin:center}.seu-qv__img.is-variant-pop{animation:seuQvVariantPop .28s cubic-bezier(.2,.85,.3,1.25)}@keyframes seuQvVariantPop{0%{transform:scale(1);opacity:1}45%{transform:scale(.94);opacity:.72}100%{transform:scale(1);opacity:1}}.seu-qv h3{font-size:19px;line-height:1.15;margin:4px 0 8px}.seu-qv__price{font-size:16px;margin-bottom:13px}.seu-qv__price s{color:#999;margin-left:6px}.seu-qv__group{margin:13px 0}.seu-qv__label{font-size:13px;font-weight:600;margin-bottom:7px}.seu-qv__opts{display:flex;flex-wrap:wrap;gap:7px}.seu-qv__opt{min-width:44px;padding:9px 12px;border:1px solid #ddd;background:#fff;border-radius:7px;font-size:12px}.seu-qv__opt.is-selected{background:#111;color:#fff;border-color:#111}.seu-qv__opt:disabled{opacity:.35;text-decoration:line-through}.seu-qv__atc{width:100%;height:48px;border:0;border-radius:8px;background:#111;color:#fff;font-size:14px;font-weight:700;margin-top:15px}.seu-qv__atc:disabled{opacity:.45}.seu-qv__full{display:block;text-align:center;color:#555;font-size:12px;margin-top:12px;text-decoration:underline;text-underline-offset:3px}
  @media(min-width:750px){.seu-qv-backdrop{align-items:center}.seu-qv{border-radius:18px;padding:20px 22px 24px;transform:translateY(14px) scale(.98)}.seu-qv-backdrop.is-open .seu-qv{transform:none}.seu-qv__body{grid-template-columns:210px 1fr}.seu-qv__img{width:210px;height:210px}}
  @media(max-width:480px){.seu-qv__body{grid-template-columns:112px 1fr;gap:14px}.seu-qv__img{width:112px;height:128px}.seu-qv h3{font-size:17px}.seu-quick-add{height:23px;min-width:0;padding:0 6px;font-size:9px;gap:3px}.seu-quick-add svg{width:10px;height:10px}}
  `; document.head.appendChild(css);
  var modal, data, selected=[];
  function money(c){return new Intl.NumberFormat(document.documentElement.lang||'en',{style:'currency',currency:(window.Shopify&&Shopify.currency&&Shopify.currency.active)||'EUR'}).format((Number(c)||0)/100)}
  function variant(){return data.variants.find(function(v){return v.options.every(function(o,i){return o===selected[i]})}) || data.variants.find(function(v){return v.available}) || data.variants[0]}
  function renderState(animate){var v=variant(); if(!v)return; var im=modal.querySelector('.seu-qv__img'); var nextImage=v.image||data.image||''; if(nextImage&&im.src!==nextImage){im.src=nextImage;if(animate){im.classList.remove('is-variant-pop');void im.offsetWidth;im.classList.add('is-variant-pop')}} modal.querySelector('.seu-qv__price').innerHTML='<b>'+money(v.price)+'</b>'+(v.compare>v.price?' <s>'+money(v.compare)+'</s>':''); modal.querySelectorAll('[data-qv-i]').forEach(function(b){b.classList.toggle('is-selected',selected[+b.dataset.qvI]===b.dataset.qvV)}); var atc=modal.querySelector('.seu-qv__atc'); atc.disabled=!v.available; atc.textContent=v.available?'Add to cart':'Sold out';}
  function open(card){try{data=JSON.parse(card.querySelector('[data-seu-quick-data]').textContent)}catch(e){return} selected=(data.variants.find(function(v){return v.available})||data.variants[0]).options.slice(); modal=document.createElement('div'); modal.className='seu-qv-backdrop'; var isDefaultOnly=data.variants.length===1&&data.variants[0].options&&data.variants[0].options.length===1&&String(data.variants[0].options[0]).toLowerCase()==='default title'; var groups=isDefaultOnly?'':(data.options||[]).map(function(name,i){var vals=[];data.variants.forEach(function(v){if(vals.indexOf(v.options[i])<0)vals.push(v.options[i])});return '<div class="seu-qv__group"><div class="seu-qv__label">'+name+'</div><div class="seu-qv__opts">'+vals.map(function(x){return '<button type="button" class="seu-qv__opt" data-qv-i="'+i+'" data-qv-v="'+String(x).replace(/"/g,'&quot;')+'">'+x+'</button>'}).join('')+'</div></div>'}).join(''); modal.innerHTML='<div class="seu-qv" role="dialog" aria-modal="true"><div class="seu-qv__top"><button class="seu-qv__close" aria-label="Close">×</button></div><div class="seu-qv__body"><img class="seu-qv__img" alt=""><div><h3></h3><div class="seu-qv__price"></div>'+groups+'<button class="seu-qv__atc">Add to cart</button><a class="seu-qv__full">View full product</a></div></div></div>'; document.body.appendChild(modal); modal.querySelector('h3').textContent=data.title; modal.querySelector('.seu-qv__img').src=data.image||''; modal.querySelector('.seu-qv__full').href=data.url; renderState(); requestAnimationFrame(function(){modal.classList.add('is-open')}); document.documentElement.style.overflow='hidden';
    modal.addEventListener('click',function(e){if(e.target===modal||e.target.closest('.seu-qv__close'))return close();var b=e.target.closest('[data-qv-i]');if(b){selected[+b.dataset.qvI]=b.dataset.qvV;renderState(true);return}if(e.target.closest('.seu-qv__atc')){var v=variant();if(!v||!v.available)return;var btn=e.target.closest('.seu-qv__atc');btn.disabled=true;btn.textContent='Adding…'; if(window.ClyroShop&&ClyroShop.add){ClyroShop.add(String(v.id),1).then(function(){btn.textContent='✓ Added';setTimeout(close,650)}).catch(function(){btn.disabled=false;btn.textContent='Add to cart'})}else{fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:[{id:String(v.id),quantity:1}]})}).then(function(){btn.textContent='✓ Added';setTimeout(close,650)})}}});
  }
  function close(){if(!modal)return;modal.classList.remove('is-open');document.documentElement.style.overflow='';var m=modal;modal=null;setTimeout(function(){m.remove()},240)}
  document.addEventListener('click',function(e){var b=e.target.closest('[data-seu-quick-add]');if(!b)return;e.preventDefault();e.stopPropagation();open(b.closest('.seu-qv-card'))});
  document.addEventListener('keydown',function(e){if(e.key==='Escape')close()});
})();
